import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiArrowRight, FiShield } from 'react-icons/fi';
import supabase from '../../supabase/supabase';

/**
 * Email Confirmed Success Page
 * 
 * Shown after user clicks the email confirmation link.
 * Provides feedback that their account is now active and guides them to login.
 * Also triggers the welcome email via Edge Function.
 */
const EmailConfirmed = () => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);
  const [user, setUser] = useState(null);
  const welcomeEmailSent = useRef(false);

  useEffect(() => {
    // Check if user is already logged in after confirmation
    const checkUserAndSendWelcome = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);

        // Send welcome email (only once)
        if (!welcomeEmailSent.current) {
          welcomeEmailSent.current = true;
          try {
            await supabase.functions.invoke('api', {
              body: {
                path: 'email/welcome',
                user_id: session.user.id,
                email: session.user.email,
                full_name: session.user.user_metadata?.full_name
              }
            });
            console.log('✅ Welcome email triggered');
          } catch (err) {
            // Non-critical - don't show error to user
            console.log('⚠️ Welcome email not sent:', err);
          }
        }
      }
    };
    checkUserAndSendWelcome();

    // Auto-redirect countdown
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // If user has session, go to dashboard, otherwise login
          if (user) {
            navigate('/dashboard');
          } else {
            navigate('/login');
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, user]);

  const handleContinue = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
      >
        {/* Success Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <FiCheckCircle className="w-12 h-12 text-green-500" />
          </motion.div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Email Confirmed!
          </h1>
          <p className="text-green-100">
            Your account is now active
          </p>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Welcome to HR Compliance
            </h2>
            <p className="text-gray-600">
              Thank you for verifying your email address. You now have full access to all features.
            </p>
          </div>

          {/* Features Preview */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <FiShield className="text-blue-500" />
              What you can do now:
            </h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                Manage employee cases and records
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                Track compliance workflows
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                Invite team members to collaborate
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                Generate compliance reports
              </li>
            </ul>
          </div>

          {/* Action Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleContinue}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 hover:from-blue-700 hover:to-purple-700 transition-all"
          >
            {user ? 'Go to Dashboard' : 'Sign In to Continue'}
            <FiArrowRight />
          </motion.button>

          {/* Auto-redirect notice */}
          <p className="text-center text-sm text-gray-500 mt-4">
            Redirecting automatically in {countdown} seconds...
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default EmailConfirmed;
